import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import { ValidationService } from '@vinnova/validation';
import { distinctUntilChanged, map, Observable, tap } from 'rxjs';
import { County } from '../../../core/models/county.model';
import { Municipality } from '../../../core/models/municipality.model';
import { UtilitiesQuery } from '../../../core/queries/utilities.query';
import { UtilitiesRepository } from '../../../core/repositories/utilities.repository';

@UntilDestroy()
@Component({
  selector: 'lib-create-address',
  templateUrl: './create-address.component.html',
  styleUrls: ['./create-address.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CreateAddressComponent implements OnInit {
  @Input() form: FormGroup<unknown>;
  @Input() name: string;
  @Input() isForeign: boolean;
  InputType = InputType;
  availableMunicipalities: Municipality[];
  availableCounties: County[];
  isDisabled: boolean;

  addressErrorMessage$: Observable<string>;
  zipCodeErrorMessage$: Observable<string>;
  cityErrorMessage$: Observable<string>;

  @Input() set disabled(value: boolean) {
    this.setDisabledState(value);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }
  constructor(
    private validationService: ValidationService,
    private utilitiesQuery: UtilitiesQuery,
    private utilitiesRepository: UtilitiesRepository,
    private translocoService: TranslocoService,
  ) {}

  get address() {
    return this.form.get('address') as FormControl<string>;
  }

  get city() {
    return this.form.get('city') as FormControl<string>;
  }

  get zipCode() {
    return this.form.get('zipCode') as FormControl<string>;
  }

  get municipality() {
    return this.form.get('municipalityCode') as FormControl<string>;
  }

  get county() {
    return this.form.get('countyCode') as FormControl<string>;
  }

  ngOnInit() {
    this.setupValueChangeListeners();

    this.utilitiesRepository.municipalities$
      .pipe(
        tap(
          (municipalities) => (this.availableMunicipalities = municipalities),
        ),
        untilDestroyed(this),
      )
      .subscribe();
    this.utilitiesRepository.counties$
      .pipe(
        tap((counties) => (this.availableCounties = counties)),
        untilDestroyed(this),
      )
      .subscribe();

    this.setErrorMessage();
  }

  setErrorMessage() {
    this.addressErrorMessage$ = this.validationService
      .isInvalid(this.address)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.address.errors.required) {
            return this.translocoService.translate('required');
          }
        }),
      );

    this.zipCodeErrorMessage$ = this.validationService
      .isInvalid(this.zipCode)
      .pipe(
        map((invalid) => {
          if (!invalid) {
            return;
          }

          if (this.zipCode.errors.required) {
            return this.translocoService.translate('required');
          }

          if (this.zipCode.errors.validPostalCode) {
            return this.translocoService.translate(
              'worksite_postal_code_invalid',
            );
          }
        }),
      );

    this.cityErrorMessage$ = this.validationService.isInvalid(this.city).pipe(
      map((invalid) => {
        if (!invalid) {
          return;
        }

        if (this.city.errors.required) {
          return this.translocoService.translate('required');
        }
      }),
    );
  }

  toUpper(event) {
    const start = event.target.selectionStart;
    const end = event.target.selectionEnd;
    event.target.value = event.target.value.toUpperCase();
    event.target.setSelectionRange(start, end);
  }

  private setupValueChangeListeners() {
    this.municipality.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.availableCounties =
          this.utilitiesQuery.getCountyFromMunicipality(value);
        if (
          !this.availableCounties.some((c) => c.value === this.county.value) &&
          (this.county.value || value)
        ) {
          this.county.patchValue(this.availableCounties[0].value, {
            emitEvent: false,
          });
        }
      });

    this.county.valueChanges
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((value) => {
        this.availableMunicipalities =
          this.utilitiesQuery.getMunicipalitiesFromCounty(value);
        if (
          !this.availableMunicipalities.some(
            (m) => m.value === this.municipality.value,
          ) &&
          (this.municipality.value || value)
        ) {
          this.municipality.patchValue(this.availableMunicipalities[0].value, {
            emitEvent: false,
          });
        }
      });
  }
}
